/**
 * UpdateFlowBridge - Connects sync progress to update flow
 *
 * When an update is in progress (phase === 'syncing'), forwards sync progress
 * to UpdateFlowContext. Completes the update flow on any terminal sync status
 * (done, skipped, error) or idle when sync never started (e.g. day-skip edge).
 */

import React from 'react';
import { useUpdateFlow } from '../contexts/UpdateFlowContext';
import { useSyncProgressOptional } from '../contexts/SyncProgressContext';
import { debugWarn } from '../utils/debug';

export const UpdateFlowBridge: React.FC = () => {
  const { phase, setSyncProgress, completeSync } = useUpdateFlow();
  const syncProgress = useSyncProgressOptional();
  const lastStatusRef = React.useRef<string | null>(null);
  const wasInSyncPhasesRef = React.useRef(false);
  const completedTerminalRef = React.useRef(false);

  React.useEffect(() => {
    if (phase !== 'syncing' || !syncProgress) {
      if (phase !== 'syncing') {
        wasInSyncPhasesRef.current = false;
        completedTerminalRef.current = false;
        lastStatusRef.current = null;
      }
      return;
    }

    const { status, recordCounts, error, infoMessage } = syncProgress;

    if (lastStatusRef.current !== null && lastStatusRef.current !== status) {
      debugWarn('[UpdateFlowBridge] sync status transition', {
        from: lastStatusRef.current,
        to: status,
        recordTypeCount: Object.keys(recordCounts).length,
      });
    }

    if (status === 'phase1' || status === 'phase2') {
      wasInSyncPhasesRef.current = true;
    }

    const totalRecords = Object.values(recordCounts).reduce((a, b) => a + b, 0);

    let progress = 0;
    let message = 'Sincronizzazione dati...';

    if (status === 'phase1') {
      progress = Math.min(80, totalRecords / 50);
      message = `Caricamento catalogo... (${totalRecords} elementi)`;
    } else if (status === 'phase2') {
      progress = 85;
      message = 'Caricamento dati operativi...';
    } else if (status === 'done') {
      progress = 100;
      message = 'Sincronizzazione completata';
    } else if (status === 'skipped') {
      progress = 100;
      message = infoMessage ?? 'Sincronizzazione non necessaria.';
    } else if (status === 'error') {
      progress = 100;
      message = error ?? 'Errore durante la sincronizzazione.';
    } else if (status === 'idle' && !wasInSyncPhasesRef.current) {
      progress = 100;
      message = 'Sincronizzazione non avviata.';
    }

    setSyncProgress(progress, message);

    const shouldComplete =
      status === 'done' ||
      status === 'skipped' ||
      status === 'error' ||
      (status === 'idle' && !wasInSyncPhasesRef.current);

    if (shouldComplete && !completedTerminalRef.current) {
      completedTerminalRef.current = true;
      debugWarn('[UpdateFlowBridge] terminal sync status → completeSync', {
        status,
        wasInSyncPhases: wasInSyncPhasesRef.current,
      });
      window.setTimeout(() => completeSync(), 500);
    }

    lastStatusRef.current = status;
  }, [phase, syncProgress, setSyncProgress, completeSync]);

  return null;
};
